import { normalizeDateStartInput } from "~/utils/isoWeek";

function parseIsoDate(value: string | null | undefined): Date | null {
  const normalized = normalizeDateStartInput(value);
  if (!normalized) {
    return null;
  }
  const [year, month, day] = normalized.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Короткая дата для date-picker: «03.06.2026». */
export function formatDateShort(value: string | null | undefined): string {
  const date = parseIsoDate(value);
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDateDayMonthLabel(value: string | null | undefined, includeYear = false): string {
  const date = parseIsoDate(value);
  if (!date) {
    return "";
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  };

  if (includeYear) {
    options.year = "numeric";
  }

  return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

/** Месяц в родительном падеже («мая»), как в «10 мая». */
function formatMonthLabelGenitive(date: Date): string {
  const parts = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
  }).formatToParts(date);
  const monthPart = parts.find((part) => part.type === "month");
  return monthPart?.value.replace(/\.$/, "").trim() ?? "";
}

/** Период недели: «4 - 10 мая» (как в конструкторе программы). */
export function formatCompactWeekDateRangeLabel(
  dateStart: string | null | undefined,
  dateEnd: string | null | undefined,
): string {
  const start = parseIsoDate(dateStart);
  const end = parseIsoDate(dateEnd);
  if (!start || !end) {
    return "";
  }

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

  if (sameMonth) {
    const monthLabel = formatMonthLabelGenitive(end);
    return monthLabel ? `${start.getUTCDate()} - ${end.getUTCDate()} ${monthLabel}` : "";
  }

  if (sameYear) {
    return `${formatDateDayMonthLabel(dateStart)} - ${formatDateDayMonthLabel(dateEnd)}`;
  }

  return `${formatDateDayMonthLabel(dateStart, true)} - ${formatDateDayMonthLabel(dateEnd, true)}`;
}

function formatUtcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function diffUtcDays(from: string, to: string): number {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (!start || !end) {
    return 0;
  }
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

/** Ближе границы недели — дни; от 7 дней — недели (единица планирования программы). */
const RELATIVE_WEEK_THRESHOLD_DAYS = 7;

function formatRussianDaysCount(count: number, mode: "future" | "past"): string {
  const abs = Math.abs(count);
  if (abs <= 0) {
    return "сегодня";
  }

  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let word = "дней";
  if (mod10 === 1 && mod100 !== 11) {
    word = "день";
  } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    word = "дня";
  }

  if (mode === "future") {
    return `через ${abs} ${word}`;
  }
  return `${abs} ${word} назад`;
}

function formatRussianWeeksCount(weeks: number, mode: "future" | "past"): string {
  const abs = Math.abs(weeks);
  if (abs <= 0) {
    return "сегодня";
  }

  const mod10 = abs % 10;
  const mod100 = abs % 100;

  if (mode === "future") {
    let word = "недель";
    if (mod10 === 1 && mod100 !== 11) {
      word = "неделю";
    } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
      word = "недели";
    }
    return `через ${abs} ${word}`;
  }

  if (mod10 === 1 && mod100 !== 11) {
    return `${abs} неделя назад`;
  }
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return `${abs} недели назад`;
  }
  return `${abs} недель назад`;
}

/** «6 недель назад» / «Через 12 недель» — смещение в ISO-неделях от опорной. */
export function formatRelativeIsoWeeksOffsetLabel(weekOffset: number): string {
  if (!Number.isFinite(weekOffset) || weekOffset === 0) {
    return "";
  }

  if (weekOffset > 0) {
    const label = formatRussianWeeksCount(weekOffset, "future");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  return formatRussianWeeksCount(Math.abs(weekOffset), "past");
}

function daysToRoundedWeekCount(days: number): number {
  return Math.max(1, Math.round(days / 7));
}

function formatRelativeWeekOffset(days: number, mode: "future" | "past"): string {
  if (days <= 0) {
    return "сегодня";
  }
  if (days < RELATIVE_WEEK_THRESHOLD_DAYS) {
    return formatRussianDaysCount(days, mode);
  }
  return formatRussianWeeksCount(daysToRoundedWeekCount(days), mode);
}

/**
 * Относительная дистанция до/от календарной недели:
 * дни у границы (меньше 7), иначе недели; внутри пн–вс — «активна».
 */
export function formatWeekCalendarRelativeLabel(
  dateStart: string | null | undefined,
  dateEnd: string | null | undefined,
): string {
  const start = normalizeDateStartInput(dateStart);
  const end = normalizeDateStartInput(dateEnd);
  if (!start || !end) {
    return "";
  }

  const today = formatUtcToday();
  if (today < start) {
    return formatRelativeWeekOffset(diffUtcDays(today, start), "future");
  }
  if (today > end) {
    return formatRelativeWeekOffset(diffUtcDays(end, today), "past");
  }
  return "активна";
}

/** Полная подпись: «4 - 10 мая (активна)». */
export function formatWeekDatesOverviewLabel(
  dateStart: string | null | undefined,
  dateEnd: string | null | undefined,
): string {
  const period = formatCompactWeekDateRangeLabel(dateStart, dateEnd);
  if (!period) {
    return "";
  }
  const relative = formatWeekCalendarRelativeLabel(dateStart, dateEnd);
  return relative ? `${period} (${relative})` : period;
}
